import Head from "next/head";
import Image from "next/image";
import CookieBanner from "@/components/CookieBanner";
import Footer from "@/components/common/Footer";
import Header from "@/components/common/Header";
import dynamic from "next/dynamic";
const LazyPolaroidCamera = dynamic(
  () => import("@/components/lazy/LazyPolaroidCamera"),
  { ssr: false },
);

import FeedbackSection from "@/components/homepage/Feedbacks";
import FaqCamera from "@/components/homepage/FaqCamera";

import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation } from "next-i18next";
import IconCheck from "@/assets/icons/iconCheck.svg";
import IconShipping from "@/assets/icons/iconShipping.svg";
import IconStar from "@/assets/icons/iconStar.svg";
import IconStarHalf from "@/assets/icons/iconStarHalf.svg";

export default function Home() {
  const { t } = useTranslation("common");
  const productSchema = {
    "@context": "https://schema.org/",
    "@type": "Product",
    name: "Polaroid Kamera mieten",
    description: "Finde deine Polaroid Kamera für dein Event.",
    review: {
      "@type": "Review",
      author: {
        "@type": "Person",
        name: "Name",
      },
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: 4.99,
      reviewCount: 15,
    },
  };

  return (
    <>
      <Head>
        <title>
          ᐅ Polaroid Kamera mieten für 15 € | mit Filme | FotoboxGuru
        </title>
        <meta
          name="description"
          content="🥇 Polaroid Kamera für dein Event mieten ✓ Garantiere Lieferung 1 Tag vor Veranstaltung ✓ 15 € + Filme ✓ Jetzt mieten – dauert nur 3 Minuten!"
        />
        <meta
          name="keywords"
          content="Polaroid Kamera mieten, Firmenfeier, Hochzeit, Geburtstag, Veranstaltung"
        />
        <meta name="robots" content="index, follow" />
        {/* Die Seite wird mit ?ref=… aus Bannern verlinkt. Ohne
            Canonical ist jede dieser Adressen für Google eine eigene
            Seite mit demselben Inhalt. */}
        <link rel="canonical" href="https://fotoboxguru.de/polaroid-kamera-mieten" />
        <link rel="icon" href="/fav/favicon.ico" type="image/x-icon" />

        <meta property="og:locale" content="de_DE" />
        <meta
          property="og:title"
          content="ᐅ Polaroid Kamera mieten für 15 € | mit Filme | FotoboxGuru"
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:description"
          content="🥇 Polaroid Kamera für dein Event mieten ✓ Garantiere Lieferung 1 Tag vor Veranstaltung ✓ 15 € + Filme ✓ Jetzt mieten – dauert nur 3 Minuten!"
        />
        <meta
          property="og:url"
          content="https://fotoboxguru.de/polaroid-kamera-mieten"
        />
        <meta
          property="og:image"
          content="https://fotoboxguru.de/images/og-polaroid.jpg"
        />
        <meta property="og:image:width" content="1000" />
        <meta property="og:image:height" content="667" />
        <meta property="og:image:type" content="image/jpeg" />

        <meta
          name="thumbnail"
          content="https://fotoboxguru.de/images/thumb-polaroid.jpg"
        />

        {/* dangerouslySetInnerHTML ist hier Pflicht, nicht Bequemlichkeit:
            Als Kindelement schreibt React den String escaped ins HTML —
            &quot; statt " — und Google kann das JSON nicht lesen. Genau
            diese Meldung kam seit 2024 immer wieder ("Strukturierte Daten,
            für die kein Parsen möglich ist"). */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }}
        />
      </Head>
      <div className="bg-[#F9F9F9]">
        <div>
          <Header />

          <div className="bg-[#6FA68F] sticky top-0 z-30">
            <div className="mx-auto max-w-2xl px-4 py-2  lg:max-w-7xl text-white">
              <div className="flex items-center justify-center gap-2">
                <IconShipping className="w-6 text-white" />
                <span>Kostenloser Versand ab 80 Bildern</span>
              </div>
            </div>
          </div>

          <main className="mx-auto max-w-2xl px-4 py-8 sm:px-6 lg:max-w-7xl lg:px-8 lg:py-8">
            <div className="flex flex-col lg:flex-row gap-10 justify-between">
              <div className="flex flex-col lg:basis-8/12 xl:basis-5/12">
                <h1 className="text-4xl font-bold tracking-tight text-[#FF5744] text-[32px]">
                  Polaroid Kamera mieten
                </h1>
                <h2 className="text-4xl font-bold tracking-tight text-slate-900 text-[32px]">
                  Deine Polaroid Kamera für deine Veranstaltung
                </h2>

                <Image
                  src={`/images/polaroid-kamera-mieten-bg.webp`}
                  alt="Polaroid Sofortbildkamera mieten"
                  width={398}
                  height={325}
                  priority={true}
                  className="mt-7 block lg:hidden"
                />

                <div className="flex flex-col gap-3 mt-5 text-[18px] self-start mt-1">
                  <div className="flex flex-row gap-2">
                    <IconCheck className="w-6 text-[#6FA68F] self-start" />

                    <div className="w-full leading-5">
                      Kamera mieten, Filme kaufen –{" "}
                      <strong>Eine Bestellung, alles organisiert</strong>{" "}
                      <span className="text-sm text-[#8d8d8d] inline-block">
                        (Nicht genutzte Filme kannst du zurückgesenden.)
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-row gap-2">
                    <IconCheck className="w-6 text-[#6FA68F] self-start" />
                    <div className="w-full leading-5">
                      Du erhältst das <strong>neueste</strong> Polaroid Kamera
                      Modell
                    </div>
                  </div>

                  <div className="flex flex-row gap-2">
                    <IconCheck className="w-6 text-[#6FA68F] self-start" />
                    <div className="w-full leading-5">
                      Garantierte Lieferung <strong>1-3 Tage</strong> vor
                      Veranstaltung
                    </div>
                  </div>

                  <div className="flex flex-row gap-2">
                    <IconCheck className="w-6 text-[#6FA68F] self-start" />
                    <div className="w-full leading-5">
                      <strong>Kostenlose Umbuchung</strong> bis zum
                      Versandzeitpunkt
                    </div>
                  </div>

                  <div className="flex flex-row gap-2">
                    <IconCheck className="w-6 text-[#6FA68F] self-start" />
                    <div className="w-full leading-5">
                      <strong>Kamera mit Schutzhülle:</strong> Kleine
                      Missgeschicke passieren – so bleibt deine Miete stressfrei
                    </div>
                  </div>
                </div>

                <div className="flex flex-col lg:flex-row gap-3 lg:gap-5 mt-5">
                  <a
                    className="rounded-md text-center bg-[#FF5744] border-2 bg-[#FF5744] border-2 border-[#FF5744] px-2 py-2.5 text-[20px] font-semibold text-white shadow-sm hover:bg-[#2C3657] hover:border-[#2C3657] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 lg:max-w-[300px] w-full justify-items-end"
                    href="#polaroid-sofortbildkamera-waehlen"
                  >
                    Zu den Kameras
                  </a>
                  {/* 
              <a
                
                className="rounded-md text-center bg-[#ffffff] border-2 border-[#FF5744] text-[#FF5744] px-2 py-2.5 text-[20px] font-semibold shadow-sm hover:bg-[#F9F9F9] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 lg:max-w-[300px] w-full justify-items-end"
                href=""
              >
                So funktioniert es
              </a>
              */}
                </div>
                <a
                  href="https://de.trustpilot.com/review/fotoboxguru.de"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-auto mr-auto lg:ml-0 lg:mr-0"
                >
                  <div className="flex flex-row gap-1 mt-2 items-center">
                    <div className="flex flex-row">
                      <IconStar className="w-4 text-[#FF5744]" />
                      <IconStar className="w-4 text-[#FF5744]" />
                      <IconStar className="w-4 text-[#FF5744]" />
                      <IconStar className="w-4 text-[#FF5744]" />
                      <IconStarHalf className="w-4 text-[#FF5744]" />
                    </div>
                    <div className="text-sm">
                      <span className="font-bold">Hervorragend</span> (4,6
                      Sterne)
                    </div>
                  </div>
                </a>
              </div>
              <div className="hidden lg:block content-center">
                <Image
                  src={`/images/polaroid-kamera-mieten-bg.webp`}
                  alt="Polaroid Sofortbildkamera mieten"
                  width={550}
                  height={448}
                  priority={true}
                />
              </div>
            </div>

            <div className="pt-2 lg:pt-6 ">
              {/* Product grid */}
              <div className="mt-6 lg:col-span-2 lg:mt-0 xl:col-span-3">
                {/* Your content */}

                <LazyPolaroidCamera />

                <FaqCamera />

                <FeedbackSection />

                <div className="flex flex-col justify-center  align-middle">
                  <h2 className="font-bold text-[32px]">
                    Polaroid Kamera für deine Veranstaltung
                  </h2>
                  <div className="text-[16px] mt-5 footer-text">
                    <p>
                      Willst du auf deiner Hochzeit, deinem Geburtstag oder
                      einer Firmenfeier einzigartige Erinnerungen schaffen? Dann
                      solltest du unbedingt eine Polaroid Kamera mieten! Mit
                      einer Polaroid Kamera kannst du sofortige Ausdrucke deiner
                      Bilder erhalten und deine Gäste werden begeistert sein.
                    </p>
                    <p>
                      Bei FotoboxGuru helfen wir dir dabei, die perfekte
                      Polaroid Kamera für deine Veranstaltung zu finden. Ob du
                      eine klassische Sofortbildkamera suchst oder eine moderne
                      Version mit zusätzlichen Features, bei uns findest du eine
                      Vielzahl von Optionen.
                    </p>
                    <h2 className="font-bold">
                      Warum eine Polaroid Kamera mieten?
                    </h2>
                    <p>
                      Der Charme von Polaroid Kameras liegt in der Magie des
                      Sofortdrucks. Du kannst die Bilder direkt in den Händen
                      halten und deine Gäste können sie als Erinnerung mit nach
                      Hause nehmen. Es ist eine großartige Möglichkeit, die
                      Stimmung und den Spaß auf deiner Feier einzufangen.
                    </p>
                    <p>
                      Unsere Fotobox-Vergleich-Seite bietet dir eine Auswahl an
                      Anbietern, bei denen du eine Polaroid Kamera mieten
                      kannst. Wir haben die besten Optionen für dich
                      zusammengestellt, damit du die perfekte Kamera für deine
                      Bedürfnisse findest.
                    </p>
                    <h2 className="font-bold">
                      Wie miete ich eine Polaroid Kamera?
                    </h2>
                    <p>
                      Die Miete einer Polaroid Kamera ist ganz einfach. Klicke
                      einfach auf "Jetzt mieten" und gebe das Datum deiner
                      Veranstaltung ein. Wir prüfen anschließend die
                      Verfügbarkeiten und melden uns bei dir.
                    </p>
                    <p>
                      Achte darauf, dass du genügend Filme mitbestellst, um
                      sicherzustellen, dass du während deiner Veranstaltung
                      ausreichend Material hast.
                    </p>
                    <p>
                      Mache deine Feier zu einem unvergesslichen Ereignis mit
                      einer gemieteten Polaroid Kamera. Besuche jetzt
                      FotoboxGuru und finde die perfekte Kamera für deine
                      Bedürfnisse!
                    </p>
                    <ul className="list-disc mb-4 pl-5">
                      {" "}
                      <li>
                        Sofortige Ausdrucke für unmittelbare Erinnerungen
                      </li>{" "}
                      <li>Einfache Bedienung und Spaß für alle Gäste</li>{" "}
                      <li>Verschiedene Anbieter und Optionen zur Auswahl</li>{" "}
                      <li>Bequeme Buchung über unsere Website</li>
                    </ul>
                    <p>
                      Verleihe deiner Veranstaltung einen Hauch von Nostalgie
                      und buche eine Polaroid Kamera bei FotoboxGuru. Deine
                      Gäste werden begeistert sein und du wirst wertvolle
                      Erinnerungen für die Ewigkeit festhalten.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </main>

          <Footer />
        </div>
      </div>
      <CookieBanner />
    </>
  );
}

export async function getStaticProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ["common"])), // ← "common" Namespace laden
    },
  };
}
